import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import {
  AlertCircle, BookOpenText, ListChecks, Loader2, Monitor, Palette, Plus, RotateCcw, Save, Smartphone, Type, Undo2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ColorField } from '@/components/coach-guide/ColorField';
import { SizeField } from '@/components/coach-guide/SizeField';
import { SectionEditor } from '@/components/coach-guide/SectionEditor';
import { RichTextEditor } from '@/components/coach-guide/RichTextEditor';
import { CoachGuidePreview, type PreviewDevice } from '@/components/coach-guide/CoachGuidePreview';
import { logCreate, logUpdate } from '@/lib/audit-log';
import {
  DEFAULT_GUIDE, DEFAULT_THEME, DEFAULT_TYPOGRAPHY, GUIDE_LIMITS, THEME_FIELDS, TYPOGRAPHY_FIELDS, GuideConflictError,
  emptySection, fetchCoachGuide, newId, richPlain, saveCoachGuide, toEditable, toStored, validateGuide,
  type CoachGuide, type GuideSection, type SectionKind,
} from '@/lib/coach-guide';

const QUERY_KEY = ['coach-help-guide'];
const snapshot = (g: CoachGuide) => JSON.stringify(toStored(g));

type Confirm = { title: string; description: string; action: string; run: () => void } | null;
const SIZE_GROUPS = ['Page', 'Sections', 'Prompts'] as const;

export default function CoachGuidePage() {
  const queryClient = useQueryClient();
  const { data: row, isLoading, error: loadError, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchCoachGuide,
    // The draft is local; a background refetch must never clobber it.
    refetchOnWindowFocus: false,
  });

  const [draft, setDraft] = useState<CoachGuide | null>(null);
  const [baseline, setBaseline] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [device, setDevice] = useState<PreviewDevice>('desktop');
  const [showErrors, setShowErrors] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);

  const loadIntoEditor = useCallback((content: unknown) => {
    const g = toEditable(content ?? DEFAULT_GUIDE);
    setDraft(g);
    setBaseline(snapshot(g));
    setExpanded(new Set(g.sections.slice(0, 1).map((s) => s.id)));
    setShowErrors(false);
  }, []);

  // Seed the editor once, when the saved guide (or its absence) first arrives.
  useEffect(() => {
    if (!isLoading && !loadError && draft === null) loadIntoEditor(row?.content);
  }, [isLoading, loadError, row, draft, loadIntoEditor]);

  const dirty = draft !== null && snapshot(draft) !== baseline;
  const errors = useMemo(() => (draft ? validateGuide(draft) : {}), [draft]);
  const errorCount = Object.keys(errors).length;
  const visibleErrors = showErrors ? errors : {};

  // Closing the tab with unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const saveMutation = useMutation({
    mutationFn: async (content: CoachGuide) => saveCoachGuide(content, row?.revision ?? null),
    onSuccess: async (saved, content) => {
      const meta = { description: 'Updated the Coach "How to use your coach" guide', status: 'success', sections: content.sections.length };
      if (row) await logUpdate('coach_help_guide', 'default', { content: row.content, revision: row.revision }, { content, revision: saved.revision }, meta);
      else await logCreate('coach_help_guide', 'default', { content, revision: saved.revision }, meta);
      queryClient.setQueryData(QUERY_KEY, saved);
      // What was sent, not the current draft: edits made while saving stay unsaved.
      setBaseline(JSON.stringify(content));
      setShowErrors(false);
      toast.success('Guide published', { description: 'Creators see the new version next time they open the page.' });
    },
    onError: async (err: Error, content) => {
      const msg = err?.message || 'Failed to save';
      const meta = { description: 'Failed to update the Coach guide', status: 'failed', error: msg };
      if (row) await logUpdate('coach_help_guide', 'default', { content: row.content, revision: row.revision }, { content }, meta);
      else await logCreate('coach_help_guide', 'default', { content }, meta);
      if (err instanceof GuideConflictError) {
        toast.error('Guide changed elsewhere', {
          description: msg,
          duration: 15000,
          action: { label: 'Reload', onClick: reloadFromServer },
        });
      } else {
        toast.error('Could not save the guide', { description: msg });
      }
    },
  });

  async function reloadFromServer() {
    const { data } = await refetch();
    loadIntoEditor(data?.content);
    toast.message('Loaded the latest saved guide');
  }

  const save = useCallback(() => {
    if (!draft || saveMutation.isPending) return;
    if (errorCount) {
      setShowErrors(true);
      // Open every section that has a problem so it's visible.
      const bad = draft.sections.filter((s) =>
        Object.keys(errors).some((k) => k.startsWith(`sections.${s.id}.`) || s.items.some((it) => k.startsWith(`items.${it.id}.`))),
      );
      setExpanded((prev) => new Set([...prev, ...bad.map((s) => s.id)]));
      toast.error(`Fix ${errorCount} issue${errorCount === 1 ? '' : 's'} before publishing`);
      return;
    }
    saveMutation.mutate(toStored(draft));
  }, [draft, errorCount, errors, saveMutation]);

  // Cmd/Ctrl+S publishes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (dirty) save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dirty, save]);

  // ─── Draft editing helpers ────────────────────────────────────────────────
  const patch = (p: Partial<CoachGuide>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const setSections = (fn: (s: GuideSection[]) => GuideSection[]) =>
    setDraft((d) => (d ? { ...d, sections: fn(d.sections) } : d));

  const addSection = (kind: SectionKind) => {
    const s = emptySection(kind);
    setSections((list) => [...list, s]);
    setExpanded((prev) => new Set(prev).add(s.id));
    setActiveSection(s.id);
  };
  const duplicateSection = (s: GuideSection) => {
    const copy: GuideSection = { ...s, id: newId(), items: s.items.map((it) => ({ ...it, id: newId() })) };
    setSections((list) => {
      const i = list.findIndex((x) => x.id === s.id);
      return [...list.slice(0, i + 1), copy, ...list.slice(i + 1)];
    });
    setExpanded((prev) => new Set(prev).add(copy.id));
  };
  const moveSection = (i: number, dir: -1 | 1) =>
    setSections((list) => {
      const j = i + dir;
      if (j < 0 || j >= list.length) return list;
      const next = [...list];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const sectionErrors = (s: GuideSection) =>
    Object.fromEntries(
      Object.entries(visibleErrors).filter(
        ([k]) => k.startsWith(`sections.${s.id}.`) || s.items.some((it) => k.startsWith(`items.${it.id}.`)),
      ),
    );
  const styleErrorCount = Object.keys(visibleErrors).filter((k) => k.startsWith('theme.') || k.startsWith('typography.')).length;
  const contentErrorCount = Object.keys(visibleErrors).length - styleErrorCount;

  // ─── Render ───────────────────────────────────────────────────────────────
  const header = (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <BookOpenText className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">Coach Guide</h1>
          <p className="text-sm text-muted-foreground">
            Edit the “How to use your coach” page creators see in the Coach web portal, including the public access-help page.
          </p>
        </div>
      </div>
      {draft && (
        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          {dirty ? (
            <Badge variant="outline" className="border-amber-500/50 bg-amber-500/10 text-amber-600 dark:text-amber-400">Unsaved changes</Badge>
          ) : row ? (
            <span className="text-xs text-muted-foreground">
              Published {formatDistanceToNow(new Date(row.updated_at), { addSuffix: true })}
              {row.updated_by_email ? ` by ${row.updated_by_email}` : ''}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">Showing the built-in guide. Nothing published yet.</span>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={!dirty || saveMutation.isPending}
            onClick={() =>
              setConfirm({
                title: 'Discard your changes?',
                description: 'The editor goes back to the version creators currently see.',
                action: 'Discard changes',
                run: () => loadIntoEditor(row?.content),
              })
            }
          >
            <Undo2 className="mr-1.5 h-4 w-4" /> Discard
          </Button>
          <Button size="sm" disabled={!dirty || saveMutation.isPending} onClick={save} title="Ctrl/⌘ + S">
            {saveMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            Publish
          </Button>
        </div>
      )}
    </div>
  );

  if (isLoading || (!loadError && !draft)) {
    return (
      <div className="mx-auto max-w-[1600px] space-y-6">
        {header}
        <div className="grid gap-6 xl:grid-cols-2">
          <Skeleton className="h-[480px]" />
          <Skeleton className="h-[480px]" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-[1600px] space-y-6">
        {header}
        <Card className="border-destructive/50">
          <CardContent className="flex flex-col items-start gap-3 pt-6 sm:flex-row sm:items-center">
            <AlertCircle className="h-5 w-5 shrink-0 text-destructive" />
            <div className="flex-1">
              <p className="font-medium">Couldn’t load the Coach guide</p>
              <p className="text-sm text-muted-foreground">
                {loadError.message || 'Unknown error'}. If this is a new environment, the coach_help_guide migration may not be applied yet.
              </p>
            </div>
            <Button variant="outline" onClick={() => refetch()}>Try again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1600px] space-y-6">
      {header}

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* ── Editor ─────────────────────────────────────────────────── */}
        <Tabs defaultValue="content" className="min-w-0 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <TabsList>
              <TabsTrigger value="content" className="gap-1.5">
                <ListChecks className="h-4 w-4" /> Content
                {contentErrorCount > 0 && <Badge variant="destructive" className="ml-1 h-5 px-1.5">{contentErrorCount}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="style" className="gap-1.5">
                <Palette className="h-4 w-4" /> Style
                {styleErrorCount > 0 && <Badge variant="destructive" className="ml-1 h-5 px-1.5">{styleErrorCount}</Badge>}
              </TabsTrigger>
            </TabsList>
            {/* Restore original switched off for now; uncomment to bring it back.
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() =>
                setConfirm({
                  title: 'Restore the original guide?',
                  description: 'Replaces all sections, prompts, colours and text sizes in the editor with the original BMC guide. Nothing changes for creators until you publish.',
                  action: 'Restore original',
                  run: () => {
                    const g = structuredClone(DEFAULT_GUIDE);
                    setDraft(g);
                    setExpanded(new Set());
                  },
                })
              }
            >
              <RotateCcw className="mr-1.5 h-4 w-4" /> Restore original
            </Button>
            */}
          </div>

          <TabsContent value="content" className="mt-0 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Page heading</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" onFocusCapture={() => setActiveSection(null)}>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Title</label>
                  <RichTextEditor
                    singleLine
                    minRows={1}
                    value={draft.title}
                    onChange={(title) => patch({ title })}
                    baseSize={draft.typography.titleSize}
                    max={GUIDE_LIMITS.title}
                    placeholder="Page title"
                    invalid={!!visibleErrors.title}
                    ariaLabel="Title"
                  />
                  {visibleErrors.title && <p className="text-xs text-destructive">{visibleErrors.title}</p>}
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Subtitle <span className="font-normal text-muted-foreground">(optional)</span></label>
                  <RichTextEditor
                    value={draft.subtitle}
                    onChange={(subtitle) => patch({ subtitle })}
                    baseSize={draft.typography.subtitleSize}
                    max={GUIDE_LIMITS.subtitle}
                    placeholder="A line under the title"
                    invalid={!!visibleErrors.subtitle}
                    ariaLabel="Subtitle"
                  />
                  {visibleErrors.subtitle && <p className="text-xs text-destructive">{visibleErrors.subtitle}</p>}
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between gap-2 pt-1">
              <div>
                <h2 className="text-sm font-semibold">Sections</h2>
                <p className="text-xs text-muted-foreground">Shown top to bottom, in this order.</p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" disabled={draft.sections.length >= GUIDE_LIMITS.sections}>
                    <Plus className="mr-1.5 h-4 w-4" /> Add section
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuItem onClick={() => addSection('prompts')} className="items-start gap-2 py-2">
                    <ListChecks className="mt-0.5 h-4 w-4" />
                    <span>
                      <span className="block font-medium">Prompt list</span>
                      <span className="block text-xs text-muted-foreground">Prompts with a Copy button</span>
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => addSection('text')} className="items-start gap-2 py-2">
                    <Type className="mt-0.5 h-4 w-4" />
                    <span>
                      <span className="block font-medium">Text block</span>
                      <span className="block text-xs text-muted-foreground">A paragraph of tips or notes</span>
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {visibleErrors.sections && <p className="text-sm text-destructive">{visibleErrors.sections}</p>}

            {draft.sections.length === 0 && (
              <Card className="border-dashed">
                <CardContent className="py-10 text-center text-sm text-muted-foreground">
                  No sections yet. Use “Add section” to start.
                </CardContent>
              </Card>
            )}

            {draft.sections.map((s, i) => (
              <SectionEditor
                key={s.id}
                section={s}
                index={i}
                total={draft.sections.length}
                expanded={expanded.has(s.id)}
                active={activeSection === s.id}
                errors={sectionErrors(s)}
                themeAccent={draft.theme.accent}
                typography={draft.typography}
                onToggle={() => toggle(s.id)}
                onFocus={() => setActiveSection(s.id)}
                onChange={(next) => setSections((list) => list.map((x) => (x.id === s.id ? next : x)))}
                onMove={(dir) => moveSection(i, dir)}
                onDuplicate={() => duplicateSection(s)}
                onDelete={() =>
                  setConfirm({
                    title: `Delete “${richPlain(s.eyebrow).trim() || `Section ${i + 1}`}”?`,
                    description:
                      s.kind === 'prompts' && s.items.length
                        ? `This removes the section and its ${s.items.length} prompt${s.items.length === 1 ? '' : 's'}. You can still discard before publishing.`
                        : 'You can still discard before publishing.',
                    action: 'Delete section',
                    run: () => setSections((list) => list.filter((x) => x.id !== s.id)),
                  })
                }
              />
            ))}
          </TabsContent>

          <TabsContent value="style" className="mt-0 space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
                <div>
                  <CardTitle className="text-base">Text sizes</CardTitle>
                  <CardDescription>Every piece of text on the page. Each size applies everywhere that text appears.</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-muted-foreground"
                  onClick={() => patch({ typography: { ...DEFAULT_TYPOGRAPHY } })}
                >
                  <RotateCcw className="mr-1.5 h-4 w-4" /> Reset all
                </Button>
              </CardHeader>
              <CardContent className="space-y-6" onFocusCapture={() => setActiveSection(null)}>
                {SIZE_GROUPS.map((group) => (
                  <div key={group} className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group}</p>
                    <div className="grid gap-5 sm:grid-cols-2">
                      {TYPOGRAPHY_FIELDS.filter((f) => f.group === group).map((f) => (
                        <SizeField
                          key={f.key}
                          label={f.label}
                          hint={f.hint}
                          min={f.min}
                          max={f.max}
                          value={draft.typography[f.key]}
                          defaultValue={DEFAULT_TYPOGRAPHY[f.key]}
                          error={visibleErrors[`typography.${f.key}`]}
                          onChange={(v) => patch({ typography: { ...draft.typography, [f.key]: v } })}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
                <div>
                  <CardTitle className="text-base">Colours</CardTitle>
                  <CardDescription>Hex colours. Add two more digits for transparency, e.g. #ffffff16.</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-muted-foreground"
                  onClick={() => patch({ theme: { ...DEFAULT_THEME } })}
                >
                  <RotateCcw className="mr-1.5 h-4 w-4" /> Reset all
                </Button>
              </CardHeader>
              <CardContent className="space-y-6" onFocusCapture={() => setActiveSection(null)}>
                {SIZE_GROUPS.map((group) => (
                  <div key={group} className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group}</p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {THEME_FIELDS.filter((f) => f.group === group).map((f) => (
                        <ColorField
                          key={f.key}
                          label={f.label}
                          hint={f.hint}
                          value={draft.theme[f.key]}
                          defaultValue={DEFAULT_THEME[f.key]}
                          error={visibleErrors[`theme.${f.key}`]}
                          onChange={(v) => patch({ theme: { ...draft.theme, [f.key]: v } })}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ── Live preview ───────────────────────────────────────────── */}
        <div className="min-w-0 xl:sticky xl:top-20">
          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
              <div>
                <p className="text-sm font-semibold">Live preview</p>
                <p className="text-xs text-muted-foreground">
                  {dirty ? 'Your unpublished changes, as creators will see them.' : 'What creators see right now.'}
                </p>
              </div>
              <ToggleGroup
                type="single"
                size="sm"
                variant="outline"
                value={device}
                onValueChange={(v) => v && setDevice(v as PreviewDevice)}
                aria-label="Preview device"
              >
                <ToggleGroupItem value="desktop" aria-label="Desktop preview" className="gap-1.5 px-2.5">
                  <Monitor className="h-4 w-4" /> <span className="hidden sm:inline">Desktop</span>
                </ToggleGroupItem>
                <ToggleGroupItem value="phone" aria-label="Phone preview" className="gap-1.5 px-2.5">
                  <Smartphone className="h-4 w-4" /> <span className="hidden sm:inline">Phone</span>
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
            <div className="bg-[#050507] xl:max-h-[calc(100vh-11rem)] xl:overflow-y-auto">
              <div className={device === 'phone' ? 'mx-auto max-w-[390px]' : ''}>
                <CoachGuidePreview guide={draft} device={device} highlightId={activeSection} />
              </div>
            </div>
          </Card>
        </div>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { confirm?.run(); setConfirm(null); }}
            >
              {confirm?.action}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
